import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, TrendingUp, TrendingDown, ChevronRight } from 'lucide-react';
import { appointments } from '../data/mockData';

const PageContainer = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.background};
  padding-bottom: 80px;
  overflow-y: auto;
`;

const HeaderSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.3s ease;

  svg {
    width: 24px;
    height: 24px;
    color: ${({ theme }) => theme.colors.text};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
  }
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const Content = styled.div`
  padding: 20px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const SummaryCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 8px;
`;

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const SummaryChange = styled.div`
  font-size: 11px;
  color: ${({ $positive, theme }) => $positive ? theme.colors.successText : theme.colors.dangerText};
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  overflow-x: auto;
  padding-bottom: 8px;

  &::-webkit-scrollbar {
    height: 4px;
  }
`;

const FilterTab = styled.button`
  background: ${({ $active, theme }) => 
    $active ? theme.colors.primary : '#F0F0F0'};
  color: ${({ $active, theme }) => 
    $active ? theme.colors.white : theme.colors.text};
  border: none;
  border-radius: 20px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ $active, theme }) => 
      $active ? theme.colors.primaryDark : '#E0E0E0'};
  }
`;

const Section = styled.section`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 16px 0;
`;

const AppointmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AppointmentCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 14px;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateX(2px);
  }
`;

const AppointmentIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.info};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const AppointmentInfo = styled.div`
  flex: 1;
`;

const AppointmentPatient = styled.h3`
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 4px 0;
`;

const AppointmentDetails = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const StatusBadge = styled.span`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  background: ${({ $status, theme }) => {
    switch ($status) {
      case 'Confirmada':
        return theme.colors.success;
      case 'Pendiente':
        return theme.colors.warning;
      case 'Reprogramada':
        return theme.colors.info;
      case 'Cancelada':
        return theme.colors.danger;
      case 'Completada':
        return '#E8F5E9';
      default:
        return theme.colors.gray;
    }
  }};
  color: ${({ $status, theme }) => {
    switch ($status) {
      case 'Confirmada':
        return theme.colors.successText;
      case 'Pendiente':
        return theme.colors.warningText;
      case 'Reprogramada':
        return theme.colors.infoText;
      case 'Cancelada':
        return theme.colors.dangerText;
      case 'Completada':
        return '#2E7D32';
      default:
        return theme.colors.text;
    }
  }};
`;

const ChevronIcon = styled(ChevronRight)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.textSecondary};
  flex-shrink: 0;
`;

const MonthlyBreakdown = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const MonthRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const MonthName = styled.span`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const MonthValue = styled.span`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.primary};
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 8px;
  background: ${({ theme }) => theme.colors.gray};
  border-radius: 4px;
  margin: 0 16px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 4px;
  width: ${({ $percentage }) => $percentage}%;
  transition: width 0.3s ease;
`;

const ReporteCitas = () => {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState('Este mes');

  // Datos de resumen
  const summaryData = {
    total: appointments.length,
    confirmadas: appointments.filter(a => a.status === 'Confirmada').length,
    pendientes: appointments.filter(a => a.status === 'Pendiente').length,
    canceladas: appointments.filter(a => a.status === 'Cancelada').length,
  };

  // Datos mensuales simulados
  const monthlyData = [
    { month: 'Diciembre', value: 156, percentage: 100 },
    { month: 'Noviembre', value: 142, percentage: 91 },
    { month: 'Octubre', value: 128, percentage: 82 },
    { month: 'Septiembre', value: 134, percentage: 86 },
    { month: 'Agosto', value: 118, percentage: 76 },
    { month: 'Julio', value: 145, percentage: 93 },
  ];

  return (
    <PageContainer>
      <HeaderSection>
        <BackButton onClick={() => navigate('/reportes')}>
          <ArrowLeft />
        </BackButton>
        <Title>Detalle de Citas</Title>
      </HeaderSection>

      <Content>
        <SummaryGrid>
          <SummaryCard>
            <SummaryLabel>Total Citas</SummaryLabel>
            <SummaryValue>{summaryData.total}</SummaryValue>
            <SummaryChange $positive>
              <TrendingUp />
              +12% vs mes anterior
            </SummaryChange>
          </SummaryCard>
          <SummaryCard>
            <SummaryLabel>Confirmadas</SummaryLabel>
            <SummaryValue>{summaryData.confirmadas}</SummaryValue>
            <SummaryChange $positive>
              <TrendingUp />
              +8%
            </SummaryChange>
          </SummaryCard>
          <SummaryCard>
            <SummaryLabel>Pendientes</SummaryLabel>
            <SummaryValue>{summaryData.pendientes}</SummaryValue>
            <SummaryChange $positive={false}>
              <TrendingDown />
              -5%
            </SummaryChange>
          </SummaryCard>
          <SummaryCard>
            <SummaryLabel>Canceladas</SummaryLabel>
            <SummaryValue>{summaryData.canceladas}</SummaryValue>
            <SummaryChange $positive={false}>
              <TrendingDown />
              -15%
            </SummaryChange>
          </SummaryCard>
        </SummaryGrid>

        <FilterTabs>
          {['Hoy', 'Esta semana', 'Este mes', 'Este año'].map((period) => (
            <FilterTab
              key={period}
              $active={selectedPeriod === period}
              onClick={() => setSelectedPeriod(period)}
            >
              {period}
            </FilterTab>
          ))}
        </FilterTabs>

        <Section>
          <SectionTitle>Desglose Mensual</SectionTitle>
          <MonthlyBreakdown>
            {monthlyData.map((item, index) => (
              <MonthRow key={index}>
                <MonthName>{item.month}</MonthName>
                <ProgressBar>
                  <ProgressFill $percentage={item.percentage} />
                </ProgressBar>
                <MonthValue>{item.value} citas</MonthValue>
              </MonthRow>
            ))}
          </MonthlyBreakdown>
        </Section>

        <Section>
          <SectionTitle>Citas Recientes</SectionTitle>
          <AppointmentsList>
            {appointments.slice(0, 5).map((appointment) => (
              <AppointmentCard 
                key={appointment.id}
                onClick={() => navigate(`/detalle-cita/${appointment.id}`)}
              >
                <AppointmentIcon>
                  <Calendar />
                </AppointmentIcon>
                <AppointmentInfo>
                  <AppointmentPatient>{appointment.patient}</AppointmentPatient>
                  <AppointmentDetails>
                    {appointment.type} • {appointment.time} • {appointment.doctor}
                  </AppointmentDetails>
                </AppointmentInfo>
                <StatusBadge $status={appointment.status}>
                  {appointment.status}
                </StatusBadge>
                <ChevronIcon />
              </AppointmentCard>
            ))}
          </AppointmentsList>
        </Section>
      </Content>
    </PageContainer>
  );
};

export default ReporteCitas;
